import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, NavigationGuardNext, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/Auth'
import { useCommunityStore } from '../stores/Community'

// --- Lazy-loaded components with chunk names and prefetch for critical views ---
const AppHeader = () =>
  import(/* webpackChunkName: "header" */ '../components/common/TopNavBar.vue')
const AppFooter = () =>
  import(/* webpackChunkName: "footer" */ '../components/common/BottomNavBar.vue')
const HomeView = () =>
  import(/* webpackChunkName: "home", webpackPrefetch: true */ '../views/HomeView.vue')
const WelfareView = () => import(/* webpackChunkName: "welfare" */ '../views/WelfareView.vue')
const EconomyView = () => import(/* webpackChunkName: "economy" */ '../views/EconomyView.vue')
const HealthView = () => import(/* webpackChunkName: "health" */ '../views/HealthView.vue')
const CommunityView = () => import(/* webpackChunkName: "community" */ '../views/CommunityView.vue')
const SigninForm = () =>
  import(/* webpackChunkName: "signin" */ '../components/user/SigninForm.vue')
const SignupForm = () =>
  import(/* webpackChunkName: "signup" */ '../components/user/SignupForm.vue')
const UserView = () => import(/* webpackChunkName: "user" */ '../views/UserView.vue')
const ComplaintManager = () =>
  import(/* webpackChunkName: "complaints" */ '../components/user/ComplaintManager.vue')
const UserProfile = () =>
  import(/* webpackChunkName: "profile" */ '../components/community/UserProfile.vue')
const PostCreate = () =>
  import(/* webpackChunkName: "post-create" */ '../components/community/PostCreate.vue')
const PostEdit = () =>
  import(/* webpackChunkName: "post-edit" */ '../components/community/PostEdit.vue')
const PostDetail = () =>
  import(/* webpackChunkName: "post-details" */ '../components/community/PostDetails.vue')
const QuestionCreate = () =>
  import(/* webpackChunkName: "question-create" */ '../components/Inquiry/QuestionCreate.vue')
const QuestionEdit = () =>
  import(/* webpackChunkName: "question-edit" */ '../components/Inquiry/QuestionEdit.vue')
const QuestionDetail = () =>
  import(/* webpackChunkName: "question-details" */ '../components/Inquiry/QuestionDetails.vue')
const QuestionList = () =>
  import(/* webpackChunkName: "question-list" */ '../components/Inquiry/QuestionList.vue')
const NotFoundView = () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue')

// --- Utility: ID validation for routes ---
function isValidId(id: string): boolean {
  return /^[a-zA-Z0-9-]+/.test(id)
}

function requireValidId(paramName: string) {
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const id = to.params[paramName] as string
    if (!isValidId(id)) return next('/')
    next()
  }
}

// --- Utility: Guard to redirect authenticated users from sign-in/up ---
function redirectIfAuthenticated(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const authStore = useAuthStore()
  if (authStore.isAuthenticated && authStore.user?.emailVerified) {
    return next('/user')
  }
  next()
}

// --- Flat routes ---
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home-view',
    components: {
      default: HomeView,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true },
  },
  {
    path: '/welfare',
    name: 'welfare-view',
    components: {
      default: WelfareView,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true },
  },
  {
    path: '/economy',
    name: 'economy-view',
    components: {
      default: EconomyView,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true },
  },
  {
    path: '/health',
    name: 'health-view',
    components: {
      default: HealthView,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true },
  },
  {
    path: '/community/:userId',
    name: 'user-profile',
    components: {
      default: UserProfile,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true, showBackIcon: true },
    props: { default: true },
    beforeEnter: requireValidId('userId'),
  },
  {
    path: '/community/:userId/:postId',
    name: 'post-details',
    components: {
      default: PostDetail,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true, showBackIcon: true },
    props: {
      default: true,
    },
    beforeEnter: requireValidId('postId'),
  },
  {
    path: '/community/:userId/:postId/edit',
    name: 'post-edit',
    components: {
      default: PostEdit,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true, showBackIcon: true },
    props: { default: true },
    beforeEnter: requireValidId('postId'),
  },
  {
    path: '/community',
    name: 'post-list',
    components: {
      default: CommunityView,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: { hasFooter: true },
  },
  {
    path: '/community/create',
    name: 'post-create',
    components: {
      default: PostCreate,
      header: AppHeader,
      footer: AppFooter,
    },
    meta: {
      hasFooter: true,
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
    },
  },
  {
    path: '/user',
    name: 'user-view',
    components: {
      default: UserView,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
  },
  {
    path: '/complaints',
    name: 'complaints',
    components: {
      default: ComplaintManager,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
  },
  {
    path: '/q-and-a/create',
    name: 'question-create',
    components: {
      default: QuestionCreate,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
  },
  {
    path: '/q-and-a/:userId/:questionId',
    name: 'question-details',
    components: {
      default: QuestionDetail,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
    props: { default: true },
    beforeEnter: requireValidId('questionId'),
  },
  {
    path: '/q-and-a/:userId/:questionId/edit',
    name: 'question-edit',
    components: {
      default: QuestionEdit,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
    props: { default: true },
    beforeEnter: requireValidId('questionId'),
  },
  {
    path: '/q-and-a',
    name: 'question-list',
    components: {
      default: QuestionList,
      header: AppHeader,
    },
    meta: {
      requiresAuth: true,
      requiresEmailVerification: true,
      showBackIcon: true,
      hasFooter: false,
    },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    components: {
      default: SigninForm,
      header: AppHeader,
    },
    meta: { hasFooter: false },
    beforeEnter: redirectIfAuthenticated,
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    components: {
      default: SignupForm,
      header: AppHeader,
    },
    meta: { hasFooter: false },
    beforeEnter: redirectIfAuthenticated,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const communityStore = useCommunityStore()

  if (!authStore.isInitialized) {
    console.log('Initializing auth for route:', to.path)
    try {
      await authStore.initializeAuth()
    } catch (e) {
      console.warn('Auth initialization failed during navigation, continue for public routes:', e)
    }
  }

  if (from.name && String(from.name).includes('community')) {
    console.log(`Cleaning up listeners when leaving ${String(from.name)}`)
    communityStore.cleanupAllListeners()
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    console.log('Not authenticated, redirecting to sign-in')
    return next({ path: '/sign-in', query: { redirect: to.fullPath } })
  }

  if (
    to.meta.requiresEmailVerification &&
    authStore.isAuthenticated &&
    authStore.user &&
    !authStore.user.emailVerified
  ) {
    console.log('Email not verified, redirecting to sign-in')
    return next('/sign-in')
  }

  next()
})

export default router
